import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  type ReactNode,
} from "react";
import {
  NavigationType,
  Routes,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
  createPath,
  parsePath,
  useLocation,
  type Location,
  type Navigator,
} from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  ExternalLink,
  ListTodo,
  Maximize2,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useSidebar } from "@/components/ui/sidebar";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoading } from "@/lib/lazy-page";
import { OfflineEditGuard } from "@/components/OfflineEditGuard";
import {
  PageHeaderProvider,
  usePageHeaderContext,
} from "@/contexts/PageHeaderContext";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useIsMobile } from "@/hooks/use-mobile";
import { NotificationsPanel } from "@/components/layout/sidebar/NotificationsPanel";
import {
  CommentsPanel,
  useUnresolvedCommentCount,
} from "@/components/layout/sidebar/CommentsPanel";
import { MyTasksPanel } from "@/components/layout/sidebar/MyTasksPanel";
import { cn } from "@/lib/utils";
import { matchContext } from "./contexts";
import { isPanelOnlyPath } from "./AppLink";
import { PanelScopeContext, usePanel, type UtilityPanel } from "./panel";
import type { PanelLocationState } from "./PanelProvider";
import { SectionPicker } from "./SectionPicker";
import { ContextActionsMenu } from "./ContextActionsMenu";
import { useContextNav } from "./useContextNav";
import { withPanelParam } from "./paths";
import type { MatchedContext } from "./types";

const WIDTH_KEY = "side-panel-width";
const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 320;
const WIDE_WIDTH = 760;
/** Narrower main views get the panel as an overlay instead (M3). */
const MIN_MAIN_WIDTH = 560;

/**
 * Panel routes that open wide by default, e.g. a signal trace (a map that
 * reads left to right). They remember their own width.
 */
const WIDE_PATTERNS = [/^\/signal-trace\//];
const WIDE_WIDTH_KEY = "side-panel-width-wide";

function widthKeyOf(path: string | null) {
  return path && WIDE_PATTERNS.some((pattern) => pattern.test(path))
    ? WIDE_WIDTH_KEY
    : WIDTH_KEY;
}

function readStoredWidth(key: string = WIDTH_KEY) {
  const stored = Number(localStorage.getItem(key));
  return Number.isFinite(stored) && stored >= MIN_WIDTH
    ? stored
    : key === WIDE_WIDTH_KEY
      ? WIDE_WIDTH
      : DEFAULT_WIDTH;
}

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

/**
 * Renders `children` as if `path` were the current location: own location,
 * own navigator. Links and navigate() inside replace the panel content (R5)
 * while hrefs stay real full-page URLs (K1, K2).
 */
function PanelRouter({
  path,
  depth,
  children,
}: {
  path: string;
  depth: number;
  children: ReactNode;
}) {
  const navigation = useContext(UNSAFE_NavigationContext);
  const mainLocation = useLocation();
  const panel = usePanel();
  const mainRef = useRef(mainLocation);
  mainRef.current = mainLocation;

  const panelLocation = useMemo<Location>(
    () => ({
      pathname: "/",
      search: "",
      hash: "",
      ...parsePath(path),
      state: null,
      key: path,
    }),
    [path],
  );

  const navigator = useMemo<Navigator>(() => {
    const outer = navigation.navigator;
    const toMain = (to: Parameters<Navigator["push"]>[0]) => {
      const href = typeof to === "string" ? to : createPath(to);
      const { pathname, search, hash } = mainRef.current;
      return withPanelParam(pathname, search, hash, href);
    };
    return {
      createHref: (to) => outer.createHref(to),
      encodeLocation: outer.encodeLocation
        ? (to) => outer.encodeLocation!(to)
        : undefined,
      go: (delta) => outer.go(delta),
      push: (to) =>
        panel.confirmDiscard(() =>
          outer.push(toMain(to), {
            panelDepth: depth + 1,
          } satisfies PanelLocationState),
        ),
      replace: (to) =>
        outer.replace(toMain(to), {
          panelDepth: depth,
        } satisfies PanelLocationState),
    };
  }, [navigation.navigator, panel, depth]);

  const navigationValue = useMemo(
    () => ({ ...navigation, navigator }),
    [navigation, navigator],
  );
  const locationValue = useMemo(
    () => ({ location: panelLocation, navigationType: NavigationType.Push }),
    [panelLocation],
  );
  const routeValue = useMemo(
    () => ({ outlet: null, matches: [], isDataRoute: false }),
    [],
  );
  const scope = useMemo(() => ({ depth }), [depth]);

  return (
    <UNSAFE_NavigationContext.Provider value={navigationValue}>
      <UNSAFE_LocationContext.Provider value={locationValue}>
        <UNSAFE_RouteContext.Provider value={routeValue}>
          <PanelScopeContext.Provider value={scope}>
            {children}
          </PanelScopeContext.Provider>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </UNSAFE_NavigationContext.Provider>
  );
}

function PanelContextPicker({
  matched,
  pathname,
}: {
  matched: MatchedContext;
  pathname: string;
}) {
  const { header, items, activeChain } = useContextNav(matched, pathname);
  if (items.length <= 1) return null;
  return (
    <SectionPicker
      variant="menu"
      title={header.title}
      items={items}
      activeChain={activeChain}
    />
  );
}

function PanelTitle({ matched }: { matched: MatchedContext | null }) {
  const { title } = usePageHeaderContext();
  return title ? (
    <span
      className="block truncate text-sm font-semibold"
      data-testid="side-panel-title"
    >
      {title}
    </span>
  ) : matched ? (
    <Skeleton className="h-4 w-32" />
  ) : null;
}

function EntityPanelHeader({
  path,
  depth,
  wide,
  onToggleWide,
}: {
  path: string;
  depth: number;
  wide: boolean;
  onToggleWide: () => void;
}) {
  const panel = usePanel();
  const isMobile = useIsMobile();
  const { pathname } = parsePath(path);
  const matched = matchContext(pathname ?? "/");
  const panelOnly = isPanelOnlyPath(pathname ?? "/");

  return (
    <div className="shrink-0 border-b px-2 py-2 space-y-1.5">
      <div className="flex items-center gap-1 min-w-0">
        {depth > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            title="Back"
            aria-label="Back in panel"
            data-testid="side-panel-back"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="flex-1 min-w-0 px-1">
          <PanelTitle matched={matched} />
        </div>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            title={wide ? "Normal width" : "Wide"}
            aria-label={wide ? "Normal width" : "Wide"}
            onClick={onToggleWide}
          >
            {wide ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
          </Button>
        )}
        {isMobile ? (
          <ContextActionsMenu
            testId="side-panel-actions-menu"
            leading={
              panelOnly ? undefined : (
                <>
                  <DropdownMenuItem
                    className="gap-2"
                    onSelect={() => panel.expand()}
                    data-testid="side-panel-expand"
                  >
                    <Maximize2 className="h-4 w-4" />
                    Open as page
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="gap-2">
                    <a
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="side-panel-new-tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open in new tab
                    </a>
                  </DropdownMenuItem>
                </>
              )
            }
          />
        ) : (
          <ContextActionsMenu testId="side-panel-actions-menu" />
        )}
        {!panelOnly && !isMobile && (
          <>
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
            >
              <a
                href={path}
                title="Open as page"
                aria-label="Open as page"
                data-testid="side-panel-expand"
                onClick={(event) => {
                  // K2: cmd/ctrl-click opens the page in a new tab.
                  if (
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.button !== 0
                  ) {
                    return;
                  }
                  event.preventDefault();
                  panel.expand();
                }}
              >
                <Maximize2 className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
            >
              <a
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                aria-label="Open in new tab"
                data-testid="side-panel-new-tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          title="Close"
          aria-label="Close panel"
          data-testid="side-panel-close"
          onClick={panel.close}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {matched && (
        <div className="px-1">
          <PanelContextPicker
            key={matched.def.type}
            matched={matched}
            pathname={pathname ?? "/"}
          />
        </div>
      )}
    </div>
  );
}

function TabBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="ml-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function UtilityPanelContent({ utility }: { utility: UtilityPanel }) {
  const panel = usePanel();
  const { currentEntity } = useRightSidebar();
  const { unreadCount } = useNotifications();
  const unresolvedCommentCount = useUnresolvedCommentCount();
  const tabs: {
    value: UtilityPanel;
    label: string;
    icon: typeof Bell;
    count?: number;
  }[] = [
    { value: "tasks", label: "Tasks", icon: ListTodo },
    {
      value: "notifications",
      label: "Notif.",
      icon: Bell,
      count: unreadCount,
    },
    ...(currentEntity
      ? [
          {
            value: "comments" as const,
            label: "Comments",
            icon: MessageSquare,
            count: unresolvedCommentCount,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b shrink-0">
        <div
          role="tablist"
          className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-muted-foreground"
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={utility === tab.value}
              onClick={() => panel.openUtility(tab.value)}
              className={cn(
                "inline-flex h-7 items-center rounded-sm px-2 text-xs font-medium transition-colors",
                utility === tab.value &&
                  "bg-background text-foreground shadow-sm",
              )}
            >
              <tab.icon className="h-3.5 w-3.5 mr-1" />
              {tab.label}
              <TabBadge count={tab.count ?? 0} />
            </button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={panel.closeUtility}
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-hidden">
        {utility === "tasks" && <MyTasksPanel />}
        {utility === "notifications" && <NotificationsPanel />}
        {utility === "comments" && <CommentsPanel />}
      </div>
    </>
  );
}

/**
 * The single right-hand panel (R1): an entity page, a create form or a
 * utility panel next to the main view. Rendered by the main layout; the
 * routes it can show are passed in so it shares them with the main view.
 */
export function SidePanel({ routes }: { routes: ReactNode }) {
  const panel = usePanel();
  const location = useLocation();
  const isMobile = useIsMobile();
  const windowWidth = useWindowWidth();
  const { state: sidebarState } = useSidebar();
  const [resizing, setResizing] = useState(false);
  const depth = (location.state as PanelLocationState | null)?.panelDepth ?? 1;

  const entityPath = panel.panelPath;
  const widthKey = widthKeyOf(entityPath);
  const [width, setWidth] = useState(() => readStoredWidth(widthKey));
  const [shownKey, setShownKey] = useState(widthKey);
  if (shownKey !== widthKey) {
    setShownKey(widthKey);
    setWidth(readStoredWidth(widthKey));
  }
  const isOpen = !!entityPath || !!panel.utility;
  const utilityOnly = !entityPath;
  const effectiveWidth = utilityOnly ? 352 : width;

  const startResize = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      setResizing(true);
      const maxWidth = Math.max(MIN_WIDTH, window.innerWidth * 0.75);
      const onMove = (move: PointerEvent) => {
        const next = Math.min(
          maxWidth,
          Math.max(MIN_WIDTH, startWidth + (startX - move.clientX)),
        );
        setWidth(next);
      };
      const onUp = () => {
        setResizing(false);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setWidth((current) => {
          localStorage.setItem(widthKey, String(Math.round(current)));
          return current;
        });
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [width, widthKey],
  );

  const toggleWide = () => {
    const next = width >= WIDE_WIDTH ? DEFAULT_WIDTH : WIDE_WIDTH;
    setWidth(next);
    localStorage.setItem(widthKey, String(next));
  };

  // Escape closes the panel unless something inside handles it.
  useEffect(() => {
    if (!entityPath) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (document.querySelector("[role=dialog], [role=alertdialog]")) return;
      panel.close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [entityPath, panel]);

  const sidebarWidth = sidebarState === "collapsed" ? 48 : 256;
  const overlay =
    !isMobile && windowWidth - sidebarWidth - effectiveWidth < MIN_MAIN_WIDTH;

  if (!isOpen) return null;

  const content = entityPath ? (
    <PageHeaderProvider>
      <PanelRouter path={entityPath} depth={depth}>
        <EntityPanelHeader
          path={entityPath}
          depth={depth}
          wide={width >= WIDE_WIDTH}
          onToggleWide={toggleWide}
        />
        <div
          className="flex-1 min-h-0 overflow-auto overscroll-contain p-3 md:p-4"
          data-testid="side-panel-content"
        >
          <ErrorBoundary key={entityPath}>
            <OfflineEditGuard>
              <Suspense fallback={<PageLoading />}>
                <Routes>{routes}</Routes>
              </Suspense>
            </OfflineEditGuard>
          </ErrorBoundary>
        </div>
      </PanelRouter>
    </PageHeaderProvider>
  ) : (
    <UtilityPanelContent utility={panel.utility!} />
  );

  if (isMobile) {
    // P4: full-screen sheet over the main view.
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col bg-background"
        data-testid="side-panel"
        data-panel={entityPath ? "entity" : panel.utility}
      >
        {content}
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "flex flex-col border-l bg-background h-full shrink-0 relative",
        // Overlay below the app header so its buttons stay reachable (M3).
        overlay && "absolute right-0 top-14 bottom-0 z-30 shadow-2xl",
        resizing && "select-none",
      )}
      style={{ width: effectiveWidth }}
      data-testid="side-panel"
      data-panel={entityPath ? "entity" : panel.utility}
    >
      {entityPath && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onPointerDown={startResize}
          className="absolute left-0 top-0 bottom-0 w-1.5 -translate-x-1/2 cursor-col-resize z-10 hover:bg-primary/20"
        />
      )}
      {content}
    </aside>
  );
}
